export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'America/New_York', label: 'America/New_York (ET)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PT)' },
  { value: 'UTC', label: 'UTC' }
];

/** 0=Sun … 6=Sat — display order Mon–Sun in UI */
export const WORKING_DAY_OPTIONS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' }
];

export const DEFAULT_WORKING_HOURS = {
  timezone: DEFAULT_TIMEZONE,
  days: [1, 2, 3, 4, 5, 6],
  start: '10:00',
  end: '19:00'
};

export function normalizeWorkingHours(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const days = Array.isArray(raw.days)
    ? [...new Set(raw.days.map((d) => Number(d)).filter((d) => d >= 0 && d <= 6))].sort(
        (a, b) => a - b
      )
    : [];
  const timezone = String(raw.timezone ?? DEFAULT_TIMEZONE).trim() || DEFAULT_TIMEZONE;
  const start = String(raw.start ?? '10:00').trim().slice(0, 5);
  const end = String(raw.end ?? '19:00').trim().slice(0, 5);
  if (!days.length) return null;
  return { timezone, days, start, end };
}

export function workingHoursEqual(a, b) {
  const left = normalizeWorkingHours(a);
  const right = normalizeWorkingHours(b);
  if (left == null && right == null) return true;
  if (left == null || right == null) return false;
  return (
    left.timezone === right.timezone &&
    left.start === right.start &&
    left.end === right.end &&
    left.days.length === right.days.length &&
    left.days.every((day, index) => day === right.days[index])
  );
}

function timeToMinutes(value) {
  const [hours, minutes] = String(value ?? '')
    .split(':')
    .map((part) => Number(part));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

export function validateWorkingHours(workingHours) {
  const normalized = normalizeWorkingHours(workingHours);
  if (!normalized) return 'Select at least one working day.';
  const startMinutes = timeToMinutes(normalized.start);
  const endMinutes = timeToMinutes(normalized.end);
  if (startMinutes == null || endMinutes == null) return 'Enter valid start and end times.';
  if (startMinutes >= endMinutes) return 'Start time must be before end time.';
  return null;
}

export function validateSendDelayMinutes(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0 || n > 1440) {
    return 'Delay must be between 0 and 1440 minutes.';
  }
  return null;
}

export function buildScheduleFormState(list) {
  const rawFlowId = list?.whatsapp_flow_id;
  const selectedFlowId =
    rawFlowId != null && rawFlowId !== '' && Number.isFinite(Number(rawFlowId))
      ? Number(rawFlowId)
      : null;
  const savedWorkingHours = normalizeWorkingHours(list?.whatsapp_flow_working_hours);
  const rawDelay = list?.whatsapp_flow_send_delay_minutes;
  const delayMinutes =
    rawDelay != null && rawDelay !== '' && Number.isFinite(Number(rawDelay)) ? Number(rawDelay) : 0;

  return {
    selectedFlowId,
    delayMinutes,
    useWorkingHours: Boolean(savedWorkingHours),
    workingHours: savedWorkingHours ? { ...savedWorkingHours } : { ...DEFAULT_WORKING_HOURS }
  };
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function formatWorkingDaysSummary(days = []) {
  if (!Array.isArray(days) || days.length === 0) return '';
  const sorted = [...new Set(days.map((d) => Number(d)).filter((d) => d >= 0 && d <= 6))].sort(
    (a, b) => a - b
  );
  if (sorted.length === 7) return 'Every day';
  return sorted.map((day) => DAY_LABELS[day] ?? day).join(', ');
}

export function formatPreleadListScheduleSummary(list) {
  if (!list?.whatsapp_flow_id) return null;
  const flowName = list?.whatsapp_flow?.name?.trim() || `Flow #${list.whatsapp_flow_id}`;
  const delay = Number(list?.whatsapp_flow_send_delay_minutes ?? 0);
  const hours = normalizeWorkingHours(list?.whatsapp_flow_working_hours);

  const parts = [flowName];
  parts.push(delay > 0 ? `${delay} min delay` : 'No delay');
  if (hours) {
    parts.push(
      `${formatWorkingDaysSummary(hours.days)} ${hours.start}–${hours.end} (${hours.timezone})`
    );
  } else {
    parts.push('Send anytime');
  }
  return parts.join(' · ');
}
